'use client';

import { Arrow } from 'icons/Arrow';
import { CircleCheck } from 'icons/CircleCheck';
import { useEffect } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';
import { Image } from '@/app/_components/common/Image';
import { Link } from '@/app/_components/common/Link';
import { RatingComponent as Rating } from '@/app/_components/NDR/Rating';
import { cn } from '@/app/_lib/utils/utils';
import type { Review } from '@/app_data/mock/ndrData';
import { trackOptimizelyEvent } from '@/app_lib/optimizely/utils';
import { trackGtmEvent } from '@/app_lib/utils/ndrUtils';
import { useOptimizely } from '@/main/lib/hooks/useOptimizely';

const ndrCompanyCardVariants = tv({
  compoundVariants: [
    {
      class: {
        optionType: 'text-blue-700 bg-blue-50',
      },
      isHighlighted: false,
      variant: 'default',
    },
  ],
  defaultVariants: {
    isHighlighted: false,
    variant: 'default',
  },
  slots: {
    companyLogo: 'h-auto',
    companyName: 'text-primary font-graphik-compact font-semibold',
    container: 'rounded border transition-colors',
    ctaButton: 'flex justify-center items-center gap-2 font-medium px-8 py-3 rounded-full transition-colors text-base',
    desktopWrapper: 'hidden lg:flex justify-between items-center gap-10 relative px-6 py-6 lg:px-10',
    featureItem: 'flex items-start gap-2',
    featuresContainer: 'flex flex-col items-start gap-3 font-graphik-compact',
    footerText: 'text-primary text-center font-graphik-compact text-sm leading-5',
    mobileWrapper: 'block lg:hidden',
    optionType:
      'text-center font-graphik-compact text-sm font-medium leading-5 flex h-[25px] px-2 py-1 items-center rounded-sm',
    phoneLink: 'underline text-blue-500',
    rankBadge:
      'text-primary font-graphik-compact text-xl font-semibold leading-6.5 flex w-10 h-10 flex-col justify-center items-center rounded-[4px_0_4px_0] bg-neutral-200',
  },
  variants: {
    isHighlighted: {
      false: {
        container: 'border-neutral-200 bg-white shadow-[0_4px_20px_0_rgba(1,1,1,0.04)]',
        ctaButton: 'text-blue-500 border border-blue-500 hover:bg-blue-50',
        optionType: 'text-blue-700 bg-blue-50',
      },
      true: {
        container: 'bg-blue-50 border-blue-500 shadow-[0_4px_20px_0_rgba(1,1,1,0.16)]',
        ctaButton: 'bg-blue-600 hover:bg-blue-700 text-white',
        optionType: 'bg-blue-800 text-blue-100',
      },
    },
    variant: {
      default: {},
      fullReview: {
        container: 'w-full',
      },
    },
  },
});

interface NdrCompanyCardProps extends VariantProps<typeof ndrCompanyCardVariants> {
  card: Review;
  className?: string;
  queryParams?: string;
  onMount: () => void;
  onResize: () => void;
}

interface NdrCompanyCardSkeletonProps {
  isHighlighted?: boolean;
}

export const NdrCompanyCard = ({
  card,
  queryParams,
  isHighlighted = false,
  variant = 'default',
  className,
  onMount,
  onResize,
}: NdrCompanyCardProps) => {
  const { optimizely } = useOptimizely();

  const slots = ndrCompanyCardVariants({ isHighlighted, variant });
  const telLink = `tel:${card.companyPhone || ''}`;
  const valuesProps = variant === 'fullReview' ? card.fullReviewProps : card.valuesProps;

  useEffect(() => {
    onMount();
  }, [onMount]);

  useEffect(() => {
    onResize();
  }, [onResize]);

  return (
    <div
      className={cn(slots.container(), className)}
      data-cy='ndr-company-card'
    >
      {/* Mobile Layout */}
      <div className={slots.mobileWrapper()}>
        {/* Header - Rank and Rating */}
        <div className='flex pr-4 justify-between items-start mb-2'>
          <div className='flex items-center gap-2'>
            <div
              className={slots.rankBadge()}
              data-cy='ndr-company-card-ordinal'
            >
              {card.ordinal}
            </div>
            <div
              className={cn(slots.optionType(), card.ordinal === 1 ? 'bg-blue-800 text-blue-100' : '')}
              data-cy='ndr-company-card-option-type'
            >
              {card.optionType}
            </div>
          </div>
          <Rating
            className='mt-4'
            data-cy='ndr-company-card-rating'
            maxRating={5}
            orientation='vertical'
            rating={card.reviewScore}
            reviewLabel={card.reviewLabel}
            size='small'
          />
        </div>

        {/* Company Logo and Name */}
        <div className='flex items-center gap-3 px-4 mb-6'>
          <Image
            alt='NDR company logo'
            className={slots.companyLogo()}
            data-cy='ndr-company-card-logo'
            fullWidth={false}
            height={30}
            priority
            src={card.companyLogos.reviewCard || ''}
            width={50}
          />
          <div
            className={cn(slots.companyName(), 'text-xl leading-6.5')}
            data-cy='ndr-company-card-name'
          >
            {card.companyName}
          </div>
        </div>

        {/* Features */}
        <div className={cn(slots.featuresContainer(), 'px-4 mb-6 text-sm leading-5.5')}>
          {valuesProps?.map((value, index) => (
            <div
              className={slots.featureItem()}
              data-cy={`ndr-company-card-value-prop-${index}`}
              key={value}
            >
              <CircleCheck />
              <div>{value}</div>
            </div>
          ))}
        </div>

        {/* CTA Button */}
        <div className='mb-2 px-4'>
          <Link
            className={slots.ctaButton()}
            data-cy='ndr-company-card-cta'
            href={`${card.ctas.primary.url}${queryParams ? `?${queryParams}` : ''}`}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                trackOptimizelyEvent(optimizely, 'ndr_learn_more_click', {});
                trackGtmEvent(`${card.ctas.primary.url}${queryParams ? `?${queryParams}` : ''}`);
              }
            }}
            onMouseDown={() => {
              trackOptimizelyEvent(optimizely, 'ndr_learn_more_click', {});
              trackGtmEvent(`${card.ctas.primary.url}${queryParams ? `?${queryParams}` : ''}`);
            }}
          >
            {card.ctas.primary.text}
            <Arrow classNames={cn('h-[25px] w-2.5', isHighlighted ? 'stroke-white' : 'stroke-blue-500')} />
          </Link>
        </div>

        {/* Footer Text */}
        <div
          className={cn(slots.footerText(), 'px-4 mb-4')}
          data-cy='ndr-company-card-visit-website'
        >
          Visit the {card.companyName} website
          {card.companyPhone && (
            <>
              &nbsp; or call:
              <Link
                className={cn(slots.phoneLink(), 'ml-0.5 whitespace-nowrap')}
                data-cy='ndr-company-card-phone'
                href={telLink}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    trackGtmEvent(telLink);
                    trackOptimizelyEvent(optimizely, 'ndr_call_click', {});
                  }
                }}
                onMouseDown={() => {
                  trackGtmEvent(telLink);
                  trackOptimizelyEvent(optimizely, 'ndr_call_click', {});
                }}
              >
                {card.companyPhone}
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Desktop Layout */}
      <div className={slots.desktopWrapper()}>
        {/* Left Content */}
        <div className='pt-6 lg:pt-0 flex-1'>
          {/* Rank Badge */}
          <div className='flex items-center gap-2 absolute top-0 left-0'>
            <div
              className={slots.rankBadge()}
              data-cy='ndr-company-card-ordinal'
            >
              {card.ordinal}
            </div>
            <div
              className={cn(slots.optionType(), card.ordinal === 1 ? 'bg-blue-800 text-blue-100' : '')}
              data-cy='ndr-company-card-option-type'
            >
              {card.optionType}
            </div>
          </div>

          {/* Main Content */}
          <div className='flex items-center gap-10'>
            <div>
              <Image
                alt='NDR company logo'
                className={slots.companyLogo()}
                data-cy='ndr-company-card-logo'
                fullWidth={false}
                height={90}
                priority
                src={card.companyLogos.reviewCard || ''}
                width={150}
              />
            </div>
            <div className='flex flex-col items-start gap-6 flex-[1_0_0]'>
              <div
                className={cn(slots.companyName(), 'text-2xl leading-8')}
                data-cy='ndr-company-card-name'
              >
                {card.companyName}
              </div>
              <div className={cn(slots.featuresContainer(), 'text-primary text-sm font-normal leading-5.5')}>
                {valuesProps?.map((value, index) => (
                  <div
                    className={slots.featureItem()}
                    data-cy={`ndr-company-card-value-prop-${index}`}
                    key={value}
                  >
                    <CircleCheck />
                    <div>{value}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Right Content */}
        <div className='flex w-[176px] flex-col items-start gap-6'>
          <Rating
            className='mx-auto'
            data-cy='ndr-company-card-rating'
            maxRating={5}
            orientation='horizontal'
            rating={card.reviewScore}
            reviewLabel={card.reviewLabel}
            size='small'
          />
          <div className='flex flex-col gap-2 self-stretch'>
            <div>
              <Link
                className={slots.ctaButton()}
                data-cy='ndr-company-card-cta'
                href={`${card.ctas.primary.url}${queryParams ? `?${queryParams}` : ''}`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    trackOptimizelyEvent(optimizely, 'ndr_learn_more_click', {});
                    trackGtmEvent(`${card.ctas.primary.url}${queryParams ? `?${queryParams}` : ''}`);
                  }
                }}
                onMouseDown={() => {
                  trackOptimizelyEvent(optimizely, 'ndr_learn_more_click', {});
                  trackGtmEvent(`${card.ctas.primary.url}${queryParams ? `?${queryParams}` : ''}`);
                }}
              >
                {card.ctas.primary.text}
                <Arrow classNames={cn('h-[25px] w-2.5', isHighlighted ? 'stroke-white' : 'stroke-blue-500')} />
              </Link>
            </div>
            <div
              className={cn(slots.footerText(), 'px-4 lg:mb-4')}
              data-cy='ndr-company-card-visit-website'
            >
              Visit the {card.companyName} website
              {card.companyPhone && (
                <>
                  &nbsp; or call:
                  <Link
                    className={cn(slots.phoneLink(), 'ml-0.5')}
                    data-cy='ndr-company-card-phone'
                    href={telLink}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        trackGtmEvent(telLink);
                      }
                    }}
                    onMouseDown={() => {
                      trackGtmEvent(telLink);
                    }}
                  >
                    {card.companyPhone}
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const NdrCompanyCardSkeleton = ({ isHighlighted = false }: NdrCompanyCardSkeletonProps) => {
  const skeletonFeatures = ['feature-1', 'feature-2', 'feature-3'];
  const skeletonStars = ['star-1', 'star-2', 'star-3', 'star-4', 'star-5'];

  return (
    <div
      className={cn(
        'flex justify-between items-center gap-10 relative px-6 py-6 lg:px-10 rounded border animate-pulse',
        isHighlighted
          ? 'bg-blue-50 border-blue-300 shadow-[0_4px_20px_0_rgba(1,1,1,0.16)]'
          : 'border-gray-200 bg-white shadow-[0_4px_20px_0_rgba(1,1,1,0.04)]',
      )}
      data-cy='ndr-company-card-skeleton'
    >
      {/* Rank Badge */}
      <div className='flex items-center gap-2 absolute top-0 left-0'>
        <div className='w-10 h-10 bg-gray-200 rounded-[4px_0_4px_0]' />
        <div className='h-[25px] w-20 bg-gray-200 rounded-sm' />
      </div>

      {/* Main Content */}
      <div className='pt-6 lg:pt-0 flex-1'>
        <div className='flex items-center gap-10'>
          {/* Company Logo */}
          <div className='w-[200px] h-[120px] bg-gray-200 rounded' />

          {/* Company Details */}
          <div className='flex flex-col items-start gap-6 flex-1'>
            {/* Company Name */}
            <div className='h-8 bg-gray-200 rounded w-48' />

            {/* Features List */}
            <div className='flex flex-col items-start gap-3 w-full'>
              {skeletonFeatures.map((featureId) => (
                <div
                  className='flex items-start gap-2 w-full'
                  key={featureId}
                >
                  <div className='w-5 h-5 bg-gray-200 rounded-full mt-0.5 flex-shrink-0' />
                  <div className='h-5 bg-gray-200 rounded flex-1 max-w-md' />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Rating and CTA Section */}
      <div className='flex w-[252px] flex-col items-start gap-6'>
        {/* Rating Component Skeleton */}
        <div className='mx-auto flex items-start border border-gray-200 rounded'>
          <div className='w-17 h-17 bg-gray-200 rounded-l' />
          <div className='flex flex-col gap-1 px-4 py-2 items-center justify-center'>
            <div className='h-4 bg-gray-200 rounded w-16' />
            <div className='flex items-center gap-2'>
              {skeletonStars.map((starId) => (
                <div
                  className='w-6 h-6 bg-gray-200 rounded'
                  key={starId}
                />
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className='flex flex-col gap-2 self-stretch'>
          {/* Primary CTA Button */}
          <div className='h-12 bg-gray-200 rounded-full' />

          {/* Secondary Text */}
          <div className='space-y-1'>
            <div className='h-4 bg-gray-200 rounded mx-auto w-3/4' />
            <div className='h-4 bg-gray-200 rounded mx-auto w-1/2' />
          </div>
        </div>
      </div>
    </div>
  );
};
